/**
 * MobileApp.jsx — Celestia Mobile Shell (Full Build)
 * All sections fully mobilised. Replace previous MobileApp.jsx with this file.
 */

import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  collection, addDoc, updateDoc, deleteDoc, setDoc,
  doc, onSnapshot, serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { computeFinanceSummary } from "../utils/financeSummary";
import { getInstructionConfig, suggestNextSiNumber } from "./InstructionFormatPanel";

// Self-contained desktop sections reused on mobile (each loads its own data and
// needs only project/user context — so they drop straight into the shell).
import {
  TEST_TYPES, TEST_TYPE_META, RESULT_COLORS, COMPACTION_LAYERS,
  computeTestStatus, testLocationLabel, blankForm, emptyPosition, emptyCube,
} from "./QualityTestsSection";
import DrawingRegisterSection from "./DrawingRegisterSection";
import DefectsSection from "./DefectsSection";
import MaterialsOnSiteSection from "./MaterialsOnSiteSection";
import CompletionDefectsSection from "./CompletionDefectsSection";
import TimeBarSection from "./TimeBarSection";
import VisualProgressTracker from "./VisualProgressTracker";

// ─── Section groups ────────────────────────────────────────────────────────────
// Resident-engineer focused: the site register the RE works from daily leads,
// with commercial / back-office sections grouped further down.
const GROUPS = [
  { id:"site",       label:"Site Register", color:"#60a5fa", sections:[
    { key:"instructions", label:"Instructions", icon:"!" },
    { key:"quality",      label:"Quality",      icon:"⚗" },
    { key:"diary",        label:"Diary",        icon:"✎" },
    { key:"rfis",         label:"RFIs",         icon:"?" },
    { key:"ncr",          label:"NCRs",         icon:"⚑" },
    { key:"defects",      label:"Defects",      icon:"⊘" },
    { key:"mos",          label:"Materials",    icon:"⬡" },
    { key:"completion",   label:"Completion",   icon:"✔" },
  ]},
  { id:"technical",  label:"Technical",   color:"#34d399", sections:[
    { key:"drawings",    label:"Drawings",     icon:"▤" },
    { key:"boq",         label:"BOQ",          icon:"⊞" },
    { key:"soqRegister", label:"SOQ Register", icon:"≡" },
    { key:"soqImport",   label:"SOQ Import",   icon:"↑" },
    { key:"contract",    label:"Contract",     icon:"⊡" },
  ]},
  { id:"progress",   label:"Progress",    color:"#f59e0b", sections:[
    { key:"home",           label:"Home",         icon:"⌂" },
    { key:"projectTracker", label:"Tracker",      icon:"◎" },
    { key:"programme",      label:"Programme",    icon:"▦" },
    { key:"visualProgress", label:"Visual Progress", icon:"◔" },
    { key:"timeBars",       label:"Time Bars",    icon:"⏱" },
  ]},
  { id:"commercial", label:"Commercial",  color:"#fb923c", sections:[
    { key:"financial",    label:"Financial",    icon:"₿" },
    { key:"certificates", label:"Certificates", icon:"✦" },
    { key:"cashflow",     label:"Cashflow",     icon:"⟁" },
    { key:"variations",   label:"Variations",   icon:"Δ" },
    { key:"claims",       label:"Claims",       icon:"⊕" },
    { key:"cpa",          label:"CPA",          icon:"⊞" },
    { key:"roe",          label:"ROE",          icon:"⊟" },
  ]},
  { id:"compliance", label:"Compliance",  color:"#e879f9", sections:[
    { key:"env",    label:"Environmental", icon:"◌" },
    { key:"hs",     label:"H&S",           icon:"+" },
    { key:"risk",   label:"Risk",          icon:"⚠" },
    { key:"audit",  label:"Audit Trail",   icon:"≋" },
    { key:"claude", label:"AI Review",     icon:"✧" },
    { key:"users",  label:"Users",         icon:"⊛", adminOnly:true },
  ]},
];

const ALL_SECTIONS = GROUPS.flatMap(g => g.sections.map(s=>({...s,groupColor:g.color,groupLabel:g.label})));
const getSectionMeta = key => ALL_SECTIONS.find(s=>s.key===key)||{label:key,icon:"◈",groupColor:"#f59e0b"};
// Bottom nav = the RE's daily-use tools.
const PINNED = [
  {key:"home",label:"Home",icon:"⌂"},
  {key:"instructions",label:"SI",icon:"!"},
  {key:"quality",label:"Quality",icon:"⚗"},
  {key:"diary",label:"Diary",icon:"✎"},
];

// ─── Utilities ─────────────────────────────────────────────────────────────────
const n0  = v => { const n=Number(String(v||"").replace(/[^0-9.-]/g,"")); return isFinite(n)?n:0; };
const fmt  = v => "R "+n0(v).toLocaleString("en-ZA",{minimumFractionDigits:0,maximumFractionDigits:0});
const fmtC = v => { const a=n0(v); if(Math.abs(a)>=1e6) return `R${(a/1e6).toFixed(1)}M`; if(Math.abs(a)>=1e3) return `R${(a/1e3).toFixed(0)}K`; return `R${a.toFixed(0)}`; };

// ─── Primitives ────────────────────────────────────────────────────────────────
function StatChip({label,value,accent}){
  return(
    <div className="pm-stat-chip" style={{"--accent":accent||"#f59e0b"}}>
      <span className="pm-stat-chip__label">{label}</span>
      <span className="pm-stat-chip__value">{value||"—"}</span>
    </div>
  );
}

function SCard({title,children,pad}){
  return(
    <div className="pm-section-card">
      {title&&<div className="pm-section-card__head"><span className="pm-section-card__title">{title}</span></div>}
      <div style={pad===false?{}:{padding:"0 14px 12px"}}>{children}</div>
    </div>
  );
}

function DataRow({label,value,mono,accent}){
  return(
    <div className="pm-data-row">
      <span className="pm-data-row__label">{label}</span>
      <span className={`pm-data-row__value${mono?" mono":""}${accent?" accent":""}`}>{value||"—"}</span>
    </div>
  );
}

function Field({label,helper,children}){
  return(
    <div className="pm-field">
      <label className="pm-field__label">{label}</label>
      {helper&&<span className="pm-field__helper">{helper}</span>}
      <div className="pm-field__input">{children}</div>
    </div>
  );
}

function TabBar({tabs,active,onChange}){
  return(
    <div className="pm-tab-bar">
      {tabs.map(t=>(
        <button key={t.key} className={`pm-tab${active===t.key?" active":""}`} onClick={()=>onChange(t.key)}>
          {t.label}{t.count!=null&&<span className="pm-tab__count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

function FilterChips({options,active,onChange}){
  return(
    <div className="pm-filter-row">
      {options.map(o=><button key={o} className={`pm-chip${active===o?" active":""}`} onClick={()=>onChange(o)}>{o}</button>)}
    </div>
  );
}

function RegItem({primary,secondary,tag,tagColor,meta,onView,onEdit,onDelete,extraActions}){
  const [open,setOpen]=useState(false);
  return(
    <div className={`pm-reg-item${open?" open":""}`}>
      <div className="pm-reg-item__main" onClick={()=>setOpen(!open)}>
        <div className="pm-reg-item__left">
          <span className="pm-reg-item__primary">{primary}</span>
          {secondary&&<span className="pm-reg-item__secondary">{secondary}</span>}
        </div>
        <div className="pm-reg-item__right">
          {tag&&<span className="pm-reg-item__tag" style={{"--tag-color":tagColor||"#f59e0b"}}>{tag}</span>}
          <span className="pm-reg-item__chevron">{open?"▲":"▼"}</span>
        </div>
      </div>
      {open&&(
        <div className="pm-reg-item__expand">
          {meta?.map((m,i)=><DataRow key={i} label={m.label} value={m.value} mono={m.mono}/>)}
          <div className="pm-reg-item__actions">
            {onView&&<button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={onView}>View</button>}
            {onEdit&&<button className="pm-btn pm-btn--blue pm-btn--sm" onClick={onEdit}>Edit</button>}
            {onDelete&&<button className="pm-btn pm-btn--red pm-btn--sm" onClick={onDelete}>Delete</button>}
            {extraActions}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({icon,title,sub}){
  return(
    <div className="pm-empty">
      <span className="pm-empty__icon">{icon||"◈"}</span>
      {title&&<p className="pm-empty__title">{title}</p>}
      {sub&&<p className="pm-empty__sub">{sub}</p>}
    </div>
  );
}

function FormDivider({label}){
  return <div className="pm-form__divider">{label}</div>;
}

// ─── HOME ──────────────────────────────────────────────────────────────────────
function HomePanel({currentProject,goTo}){
  if(!currentProject) return <EmptyState icon="⌂" title="No project selected" sub="Choose a project from the top bar."/>;
  const prog=Math.min(currentProject.progress||0,100);
  return(
    <div className="pm-home">
      <div className="pm-home__hero">
        <div className="pm-home__hero-eyebrow">
          <span className="pm-tag" style={{"--tag-color":"#f59e0b"}}>{currentProject.status||"Active"}</span>
          <span className="pm-tag pm-tag--mono">{currentProject.contractType||"GCC"}</span>
        </div>
        <h1 className="pm-home__title">{currentProject.name||currentProject.id}</h1>
        <p className="pm-home__client">{currentProject.client||""}</p>
        <div className="pm-progress">
          <div className="pm-progress__track"><div className="pm-progress__fill" style={{width:`${prog}%`}}/></div>
          <span className="pm-progress__label">{prog}%</span>
        </div>
      </div>
      <div className="pm-grid-2">
        <StatChip label="Contract Value" value={fmt(currentProject.scheduledWorks)} accent="#f59e0b"/>
        <StatChip label="Grand Total"    value={fmt(currentProject.grandTotal)}     accent="#fb923c"/>
        <StatChip label="Start"          value={currentProject.startDate||"—"}      accent="#60a5fa"/>
        <StatChip label="End"            value={currentProject.estimatedEndDate||currentProject.completionDate||"—"} accent="#34d399"/>
      </div>
      <SCard title="Contract Details" pad={false}>
        <DataRow label="Contract No."  value={currentProject.contractNumber}      mono/>
        <DataRow label="Description"   value={currentProject.contractDescription}/>
        <DataRow label="Contractor"    value={currentProject.contractor}/>
        <DataRow label="Department"    value={currentProject.department}/>
        <DataRow label="Revised End"   value={currentProject.revisedEndDate}      mono/>
        <DataRow label="Contract Type" value={currentProject.contractType}/>
      </SCard>
      <SCard title="Site Quick Capture" pad={false}>
        <div className="pm-quick-grid">
          {[
            {key:"instructions",label:"Instructions",color:"#60a5fa"},
            {key:"quality",label:"Quality",color:"#34d399"},
            {key:"diary",label:"Diary",color:"#f59e0b"},
            {key:"rfis",label:"RFIs",color:"#34d399"},
            {key:"ncr",label:"NCRs",color:"#f87171"},
            {key:"defects",label:"Defects",color:"#fb923c"},
            {key:"mos",label:"Materials",color:"#60a5fa"},
            {key:"drawings",label:"Drawings",color:"#e879f9"},
            {key:"variations",label:"Variations",color:"#fb923c"},
            {key:"certificates",label:"Certificates",color:"#f59e0b"},
          ].map(q=>(
            <button key={q.key} className="pm-quick-tile" style={{"--tile-color":q.color}} onClick={()=>goTo(q.key)}>
              <span className="pm-quick-tile__dot"/>{q.label}
            </button>
          ))}
        </div>
      </SCard>
    </div>
  );
}

// ─── FINANCIAL ─────────────────────────────────────────────────────────────────
function FinancialPanel({currentProject,boqRows=[],variations=[],claims=[],certificates=[],soqItems=[],currentProjectId}){
  const pid=currentProjectId;
  // Identical commercial position to the web Financial Dashboard (shared helper).
  const fin=computeFinanceSummary({pid,soqItems,boqRows,variations,claims,certificates});
  const c=fin.counts;
  const rows=[
    {label:"Tendered BOQ",           val:fin.tenderedBaseline,  accent:"#60a5fa"},
    {label:"Approved VO Additions",  val:fin.voAdds,            accent:"#34d399"},
    {label:"VO Omissions",           val:fin.voOmit,            accent:"#f87171",neg:true},
    {label:"SOQ Savings (zero-qty)", val:fin.soqZeroQtySavings, accent:"#f87171",neg:true},
    {label:"Revised Contract Value", val:fin.rcv,               accent:"#f59e0b",bold:true},
    {label:"Certified to Date",      val:fin.certifiedToDate,   accent:"#34d399"},
    {label:"Remaining to Certify",   val:fin.rem,               accent:"#fb923c"},
    {label:"Pending VO Exposure",    val:fin.pendingVoExp,      accent:"#fbbf24"},
    {label:"Pending Claim Exposure", val:fin.pendingClaimExp,   accent:"#f87171"},
    {label:"Forecast Final Cost",    val:fin.ffc,               accent:"#e879f9",bold:true},
  ];
  return(
    <div className="pm-panel">
      <div className="pm-grid-2">
        <StatChip label="Revised Value"  value={fmtC(fin.rcv)}            accent="#f59e0b"/>
        <StatChip label="Certified"      value={fmtC(fin.certifiedToDate)}accent="#34d399"/>
        <StatChip label="Remaining"      value={fmtC(fin.rem)}            accent="#fb923c"/>
        <StatChip label="Forecast Final" value={fmtC(fin.ffc)}            accent="#e879f9"/>
      </div>
      <SCard title="Commercial Summary" pad={false}>
        {rows.map((r,i)=>(
          <div key={i} className="pm-data-row" style={r.bold?{background:"rgba(245,158,11,0.04)"}:{}}>
            <span className="pm-data-row__label" style={r.bold?{color:"#f1f5f9",fontWeight:800}:{}}>{r.label}</span>
            <span className="pm-data-row__value mono" style={{color:r.accent,fontWeight:r.bold?800:500}}>
              {r.neg&&n0(r.val)>0?"–":""}{fmtC(r.val)}
            </span>
          </div>
        ))}
        <div className="pm-data-row">
          <span className="pm-data-row__label" style={{color:"var(--pm-text2)"}}>Certified Progress</span>
          <span className="pm-data-row__value mono" style={{color:"#60a5fa"}}>{fin.spPct.toFixed(1)}%</span>
        </div>
      </SCard>
      <SCard title="Variations" pad={false}>
        <DataRow label="Total"    value={String(c.varsTotal)}/>
        <DataRow label="Approved" value={String(c.varsApproved)}/>
        <DataRow label="Pending"  value={String(c.varsPending)}/>
        <DataRow label="Rejected" value={String(c.varsRejected)}/>
      </SCard>
      <SCard title="Claims" pad={false}>
        <DataRow label="Total"         value={String(c.claimsTotal)}/>
        <DataRow label="In Review"     value={String(c.claimsInReview)}/>
        <DataRow label="Awarded"       value={String(c.claimsAwarded)}/>
        <DataRow label="Days Awarded"  value={String(c.daysAwarded)}/>
      </SCard>
    </div>
  );
}

// ─── CERTIFICATES ──────────────────────────────────────────────────────────────
function CertificatesPanel({certificates=[],currentProjectId}){
  const [filter,setFilter]=useState("All");
  const [viewing,setViewing]=useState(null);
  const rows=certificates.filter(c=>c.projectId===currentProjectId).filter(c=>filter==="All"||c.status===filter);
  const tagColor={Issued:"#34d399",Draft:"#fbbf24",Disputed:"#f87171"};
  return(
    <div className="pm-panel">
      <FilterChips options={["All","Issued","Draft","Disputed"]} active={filter} onChange={setFilter}/>
      {rows.length===0?<EmptyState icon="✦" title="No certificates"/>:
        rows.map(c=>(
          <RegItem key={c.id}
            primary={`Cert ${c.certificateNo||c.id}`}
            secondary={c.certificateMonth||c.period||""}
            tag={c.status||"Issued"} tagColor={tagColor[c.status]||"#34d399"}
            meta={[
              {label:"Subtotal A",   value:fmt(c.subtotalA||c.totalCertified||c.certifiedAmount||0),  mono:true},
              {label:"Retention",    value:fmt(c.retention||c.retentionAmount||0),                    mono:true},
              {label:"Net Payable",  value:fmt(c.nettAmount||c.netPayable||c.netAmount||0),            mono:true},
              {label:"VAT",          value:fmt(c.vat||c.vatAmount||0),                                mono:true},
              {label:"Amount Due",   value:fmt(c.amountNowDue||c.amountDue||0),                       mono:true},
              {label:"Month",        value:c.certificateMonth||c.period||"—"},
              {label:"Cert Date",    value:c.certificateDate||"—",                                    mono:true},
            ]}
            onView={()=>setViewing(c)}
          />
        ))
      }

      {/* ── Mobile cert detail modal ── */}
      {viewing&&(
        <div style={{position:"fixed",inset:0,zIndex:700,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"flex-end"}} onClick={()=>setViewing(null)}>
          <div style={{width:"100%",maxHeight:"80vh",overflowY:"auto",background:"var(--pm-surface)",borderRadius:"18px 18px 0 0",border:"1px solid var(--pm-border2)"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderBottom:"1px solid var(--pm-border)",position:"sticky",top:0,background:"var(--pm-surface)"}}>
              <span style={{fontFamily:"var(--font-mono)",fontSize:11,letterSpacing:"0.16em",textTransform:"uppercase",color:"var(--pm-text2)"}}>
                Cert {viewing.certificateNo||viewing.id}
              </span>
              <button onClick={()=>setViewing(null)} style={{background:"var(--pm-surface2)",border:"1px solid var(--pm-border2)",borderRadius:8,color:"var(--pm-text)",width:32,height:32,cursor:"pointer"}}>✕</button>
            </div>
            <div style={{padding:"4px 0 24px"}}>
              {[
                {label:"Certificate No",  value:viewing.certificateNo||"—",                                         mono:true},
                {label:"Month",           value:viewing.certificateMonth||viewing.period||"—"},
                {label:"Date",            value:viewing.certificateDate||"—",                                        mono:true},
                {label:"Status",          value:viewing.status||"—"},
                {label:"Subtotal A",      value:fmt(viewing.subtotalA||viewing.totalCertified||0),                   mono:true},
                {label:"Subtotal B",      value:fmt(viewing.subtotalB||viewing.effectiveSubtotalB||0),               mono:true},
                {label:"CPA This Cert",   value:fmt(viewing.cpaThisCertificate||0),                                 mono:true},
                {label:"CPA Cumulative",  value:fmt(viewing.cpaCumulative||viewing.currentCpaCumulative||0),         mono:true},
                {label:"ROE",             value:fmt(viewing.roeThisCertificate||0),                                 mono:true},
                {label:"Retention",       value:fmt(viewing.retention||viewing.retentionAmount||0),                  mono:true},
                {label:"Gross Amount Due",value:fmt(viewing.grossAmountDue||0),                                      mono:true},
                {label:"Previous Pmts",   value:fmt(viewing.previousPayments||0),                                   mono:true},
                {label:"Nett Amount",     value:fmt(viewing.nettAmount||viewing.netPayable||0),                      mono:true,accent:true},
                {label:"VAT (15%)",       value:fmt(viewing.vat||viewing.vatAmount||0),                             mono:true},
                {label:"Amount Now Due",  value:fmt(viewing.amountNowDue||viewing.amountDue||0),                     mono:true,accent:true},
                {label:"Paid",            value:viewing.isPaid?"Yes":"No"},
                {label:"Notes",           value:viewing.notes||"—"},
              ].map((r,i)=><DataRow key={i} label={r.label} value={r.value} mono={r.mono} accent={r.accent}/>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── VARIATIONS ────────────────────────────────────────────────────────────────
function VariationsPanel({variations=[],currentProjectId,variationForm,setVariationForm,handleAddOrUpdateVariation,handleEditVariation,handleDeleteVariation,editingVariationId,handleViewItem}){
  const [tab,setTab]=useState("register");
  const [filter,setFilter]=useState("All");
  const rows=variations.filter(v=>v.projectId===currentProjectId).filter(v=>filter==="All"||v.status===filter);
  const tagColor={Approved:"#34d399",Rejected:"#f87171",Pending:"#fbbf24","Under Review":"#60a5fa"};
  const upd=(k,v)=>setVariationForm({...variationForm,[k]:v,projectId:currentProjectId});
  return(
    <div className="pm-panel">
      <TabBar tabs={[{key:"register",label:"Register",count:rows.length},{key:"form",label:editingVariationId?"Edit VO":"+ New VO"}]} active={tab} onChange={setTab}/>
      {tab==="register"&&<>
        <FilterChips options={["All","Pending","Under Review","Approved","Rejected"]} active={filter} onChange={setFilter}/>
        {rows.length===0?<EmptyState icon="Δ" title="No variation orders"/>:
          rows.map(v=>(
            <RegItem key={v.id}
              primary={`VO ${v.variationNo||v.id}`} secondary={v.voDescription||v.title||""}
              tag={v.status||"Pending"} tagColor={tagColor[v.status]||"#fbbf24"}
              meta={[
                {label:"Amount",  value:fmt(v.amount||v.voAmount||0),mono:true},
                {label:"Type",    value:v.voType||v.variationType||"—"},
                {label:"Date",    value:v.voDate||v.dateIssued||"—"},
                {label:"Clause",  value:v.contractClause||"—"},
              ]}
              onView={handleViewItem?()=>handleViewItem(`VO ${v.variationNo}`,v):null}
              onEdit={()=>{handleEditVariation(v);setTab("form");}}
              onDelete={()=>handleDeleteVariation(v.id)}
            />
          ))
        }
      </>}
      {tab==="form"&&(
        <div className="pm-form">
          <Field label="VO Number"><input value={variationForm.variationNo||""} onChange={e=>upd("variationNo",e.target.value)}/></Field>
          <Field label="Description"><textarea value={variationForm.voDescription||""} onChange={e=>upd("voDescription",e.target.value)}/></Field>
          <Field label="Amount (R)"><input type="number" value={variationForm.amount||""} onChange={e=>upd("amount",e.target.value)}/></Field>
          <Field label="Status">
            <select value={variationForm.status||"Pending"} onChange={e=>upd("status",e.target.value)}>
              {["Pending","Under Review","Approved","Rejected"].map(s=><option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Date Issued"><input type="date" value={variationForm.voDate||""} onChange={e=>upd("voDate",e.target.value)}/></Field>
          <Field label="Contract Clause"><input value={variationForm.contractClause||""} onChange={e=>upd("contractClause",e.target.value)}/></Field>
          <Field label="VO Type"><input value={variationForm.voType||""} onChange={e=>upd("voType",e.target.value)}/></Field>
          <Field label="Notes"><textarea value={variationForm.notes||""} onChange={e=>upd("notes",e.target.value)}/></Field>
          <button className="pm-btn pm-btn--primary pm-btn--full" onClick={()=>{handleAddOrUpdateVariation();setTab("register");}}>
            {editingVariationId?"Update VO":"Save VO"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── CLAIMS ────────────────────────────────────────────────────────────────────
function ClaimsPanel({claims=[],currentProjectId,currentProject,claimForm,setClaimForm,handleAddOrUpdateClaim,handleEditClaim,handleDeleteClaim,editingClaimId,clearClaimForm,handleViewItem}){
  const [tab,setTab]=useState("register");
  const [filter,setFilter]=useState("All");
  const rows=claims.filter(c=>c.projectId===currentProjectId).filter(c=>filter==="All"||c.status===filter);
  const tagColor={Awarded:"#34d399",Rejected:"#f87171","In Review":"#60a5fa","Time Barred":"#f87171"};
  const upd=(k,v)=>setClaimForm({...claimForm,[k]:v,projectId:currentProjectId});
  const totalClaimed=rows.reduce((s,c)=>s+n0(c.directCostClaimed||0)+n0(c.pgClaimed||0),0);
  const totalAwarded=rows.reduce((s,c)=>s+n0(c.directCostAwarded||0)+n0(c.pgAwarded||0),0);
  return(
    <div className="pm-panel">
      <div className="pm-grid-2">
        <StatChip label="Total Claimed" value={fmtC(totalClaimed)} accent="#fb923c"/>
        <StatChip label="Total Awarded" value={fmtC(totalAwarded)} accent="#34d399"/>
      </div>
      <TabBar tabs={[{key:"register",label:"Register",count:rows.length},{key:"form",label:editingClaimId?"Edit":"+ New"}]} active={tab} onChange={setTab}/>
      {tab==="register"&&<>
        <FilterChips options={["All","In Review","Awarded","Rejected","Time Barred"]} active={filter} onChange={setFilter}/>
        {rows.length===0?<EmptyState icon="⊕" title="No claims"/>:
          rows.map(c=>(
            <RegItem key={c.id}
              primary={`Claim ${c.claimNo||c.id}`} secondary={c.title||""}
              tag={c.status||"In Review"} tagColor={tagColor[c.status]||"#60a5fa"}
              meta={[
                {label:"Type",         value:c.claimType||"—"},
                {label:"Notification", value:c.latestNotificationDate||"—",mono:true},
                {label:"Submitted",    value:c.claimDate||"—",mono:true},
                {label:"Cost Claimed", value:fmt(n0(c.directCostClaimed)+n0(c.pgClaimed)),mono:true},
                {label:"Days Claimed", value:c.daysClaimed||"—"},
                {label:"Days Awarded", value:c.daysAwarded||"—"},
                {label:"Clause",       value:c.contractClause||"—"},
              ]}
              onView={handleViewItem?()=>handleViewItem(`Claim ${c.claimNo}`,c):null}
              onEdit={()=>{handleEditClaim(c);setTab("form");}}
              onDelete={()=>handleDeleteClaim(c.id)}
            />
          ))
        }
      </>}
      {tab==="form"&&(
        <div className="pm-form">
          {editingClaimId&&<button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={()=>{clearClaimForm();setTab("register");}}>← Cancel</button>}
          <Field label="Claim Number"><input value={claimForm.claimNo||""} onChange={e=>upd("claimNo",e.target.value)}/></Field>
          <Field label="Title"><input value={claimForm.title||""} onChange={e=>upd("title",e.target.value)}/></Field>
          <Field label="Claim Type">
            <select value={claimForm.claimType||"Cost and Time"} onChange={e=>upd("claimType",e.target.value)}>
              {["Cost and Time","Cost Only","Time Only","General Damages"].map(s=><option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Latest Notification Date"><input type="date" value={claimForm.latestNotificationDate||""} onChange={e=>upd("latestNotificationDate",e.target.value)}/></Field>
          <Field label="Claim Submitted Date"><input type="date" value={claimForm.claimDate||""} onChange={e=>upd("claimDate",e.target.value)}/></Field>
          <Field label="Status">
            <select value={claimForm.status||"In Review"} onChange={e=>upd("status",e.target.value)}>
              {["In Review","Awarded","Rejected","Time Barred"].map(s=><option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Contract Clause"><input value={claimForm.contractClause||""} onChange={e=>upd("contractClause",e.target.value)}/></Field>
          <FormDivider label="Cost"/>
          <Field label="Direct Cost Claimed (R)"><input type="number" value={claimForm.directCostClaimed||""} onChange={e=>upd("directCostClaimed",e.target.value)}/></Field>
          <Field label="Direct Cost Awarded (R)"><input type="number" value={claimForm.directCostAwarded||""} onChange={e=>upd("directCostAwarded",e.target.value)}/></Field>
          <FormDivider label="Time"/>
          <Field label="Days Claimed"><input type="number" value={claimForm.daysClaimed||""} onChange={e=>upd("daysClaimed",e.target.value)}/></Field>
          <Field label="Days Awarded"><input type="number" value={claimForm.daysAwarded||""} onChange={e=>upd("daysAwarded",e.target.value)}/></Field>
          <Field label="Proposed Ruling Date"><input type="date" value={claimForm.proposedRulingDate||""} onChange={e=>upd("proposedRulingDate",e.target.value)}/></Field>
          <Field label="Engineer Recommendation"><textarea value={claimForm.engineerRecommendation||""} onChange={e=>upd("engineerRecommendation",e.target.value)}/></Field>
          <Field label="Detail"><textarea value={claimForm.detail||""} onChange={e=>upd("detail",e.target.value)}/></Field>
          <button className="pm-btn pm-btn--primary pm-btn--full" onClick={()=>{handleAddOrUpdateClaim();setTab("register");}}>
            {editingClaimId?"Update Claim":"Save Claim"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── RFI ───────────────────────────────────────────────────────────────────────
function RFIPanel({rfis=[],currentProjectId,rfiForm,setRfiForm,handleAddRfi,handleEditRfi,handleDeleteRfi,editingRfiId,clearRfiForm,variations=[],handleCreateVariationFromRfi,handleViewItem}){
  const [tab,setTab]=useState("register");
  const [filter,setFilter]=useState("All");
  const rows=rfis.filter(r=>r.projectId===currentProjectId).filter(r=>filter==="All"||r.status===filter);
  const tagColor={Open:"#f87171","Under Review":"#fbbf24",Responded:"#60a5fa",Closed:"#34d399"};
  const upd=(k,v)=>setRfiForm({...rfiForm,[k]:v,projectId:currentProjectId});
  return(
    <div className="pm-panel">
      <TabBar tabs={[{key:"register",label:"Register",count:rows.length},{key:"form",label:editingRfiId?"Edit RFI":"+ New RFI"}]} active={tab} onChange={setTab}/>
      {tab==="register"&&<>
        <FilterChips options={["All","Open","Under Review","Responded","Closed"]} active={filter} onChange={setFilter}/>
        {rows.length===0?<EmptyState icon="?" title="No RFIs"/>:
          rows.map(r=>(
            <RegItem key={r.id}
              primary={`RFI ${r.rfiNo||r.id}`} secondary={r.subject}
              tag={r.status} tagColor={tagColor[r.status]}
              meta={[
                {label:"Submitted",  value:r.dateSubmitted,mono:true},
                {label:"Originator", value:r.originator},
                {label:"Discipline", value:r.discipline},
                {label:"Response By",value:r.requiredResponseDate,mono:true},
                {label:"Creates VO", value:r.responseCreatesVariation||"No"},
              ]}
              onView={handleViewItem?()=>handleViewItem(`RFI ${r.rfiNo}`,r):null}
              onEdit={()=>{handleEditRfi(r);setTab("form");}}
              onDelete={()=>handleDeleteRfi(r.id)}
              extraActions={r.responseCreatesVariation==="Yes"&&!r.relatedVariationId&&handleCreateVariationFromRfi&&(
                <button className="pm-btn pm-btn--green pm-btn--sm" onClick={()=>handleCreateVariationFromRfi(r)}>Create VO</button>
              )}
            />
          ))
        }
      </>}
      {tab==="form"&&(
        <div className="pm-form">
          {editingRfiId&&<button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={()=>{clearRfiForm();setTab("register");}}>← Cancel</button>}
          <Field label="RFI Number"><input value={rfiForm.rfiNo||""} onChange={e=>upd("rfiNo",e.target.value)}/></Field>
          <Field label="Date Submitted"><input type="date" value={rfiForm.dateSubmitted||""} onChange={e=>upd("dateSubmitted",e.target.value)}/></Field>
          <Field label="Originator"><input value={rfiForm.originator||""} onChange={e=>upd("originator",e.target.value)}/></Field>
          <Field label="Contractor"><input value={rfiForm.contractor||""} onChange={e=>upd("contractor",e.target.value)}/></Field>
          <Field label="Discipline"><input value={rfiForm.discipline||""} onChange={e=>upd("discipline",e.target.value)}/></Field>
          <Field label="Subject"><input value={rfiForm.subject||""} onChange={e=>upd("subject",e.target.value)}/></Field>
          <Field label="Reason"><textarea value={rfiForm.reason||""} onChange={e=>upd("reason",e.target.value)}/></Field>
          <Field label="Drawing / Spec Ref"><input value={rfiForm.reference||""} onChange={e=>upd("reference",e.target.value)}/></Field>
          <Field label="Required Response Date"><input type="date" value={rfiForm.requiredResponseDate||""} onChange={e=>upd("requiredResponseDate",e.target.value)}/></Field>
          <Field label="Date Responded"><input type="date" value={rfiForm.dateResponded||""} onChange={e=>upd("dateResponded",e.target.value)}/></Field>
          <Field label="Response Summary"><textarea value={rfiForm.responseSummary||""} onChange={e=>upd("responseSummary",e.target.value)}/></Field>
          <Field label="Status">
            <select value={rfiForm.status||"Open"} onChange={e=>upd("status",e.target.value)}>
              {["Open","Under Review","Responded","Closed"].map(s=><option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Impact"><input value={rfiForm.impact||""} onChange={e=>upd("impact",e.target.value)}/></Field>
          <Field label="Response Creates VO">
            <select value={rfiForm.responseCreatesVariation||"No"} onChange={e=>upd("responseCreatesVariation",e.target.value)}>
              <option>No</option><option>Yes</option>
            </select>
          </Field>
          <Field label="Notes"><textarea value={rfiForm.notes||""} onChange={e=>upd("notes",e.target.value)}/></Field>
          <button className="pm-btn pm-btn--primary pm-btn--full" onClick={()=>{handleAddRfi();setTab("register");}}>
            {editingRfiId?"Update RFI":"Save RFI"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── INSTRUCTIONS ──────────────────────────────────────────────────────────────
function InstructionsPanel({instructions=[],currentProjectId,currentProject,instructionForm,setInstructionForm,handleAddInstruction,handleEditInstruction,handleDeleteInstruction,editingInstructionId,clearInstructionForm,rfis=[],variations=[],handleCreateVariationFromInstruction,handleViewItem}){
  const [tab,setTab]=useState("register");
  const rows=instructions.filter(i=>i.projectId===currentProjectId);
  const siConfig=useMemo(()=>getInstructionConfig(currentProject),[currentProject]);
  const nextSiNumber=useMemo(()=>suggestNextSiNumber(siConfig,rows,currentProject),[siConfig,rows,currentProject]);
  const upd=(k,v)=>setInstructionForm({...instructionForm,[k]:v,projectId:currentProjectId});
  const projRfis=rfis.filter(r=>r.projectId===currentProjectId);
  return(
    <div className="pm-panel">
      <TabBar tabs={[{key:"register",label:"Register",count:rows.length},{key:"form",label:editingInstructionId?"Edit SI":"+ New SI"}]} active={tab} onChange={setTab}/>
      {tab==="register"&&(
        rows.length===0?<EmptyState icon="!" title="No instructions"/>:
        rows.map(i=>(
          <RegItem key={i.id}
            primary={`SI ${i.siNumber||i.id}`} secondary={i.instructionDescription||i.subject||""}
            tag={i.status||"—"} tagColor={i.status==="Completed"||i.status==="Closed"?"#34d399":i.status==="Cancelled"?"#f87171":"#f59e0b"}
            meta={[
              {label:"Date",      value:i.dateIssued||i.siDate||"—",mono:true},
              {label:"Issued To", value:i.issuedTo||i.contractor||"—"},
              {label:"Creates VO",value:i.createsVariation||"No"},
              {label:"Clause",    value:i.contractClause||"—"},
            ]}
            onView={handleViewItem?()=>handleViewItem(`SI ${i.siNumber}`,i):null}
            onEdit={()=>{handleEditInstruction(i);setTab("form");}}
            onDelete={()=>handleDeleteInstruction(i.id)}
            extraActions={i.createsVariation==="Yes"&&!i.relatedVariationId&&handleCreateVariationFromInstruction&&(
              <button className="pm-btn pm-btn--green pm-btn--sm" onClick={()=>handleCreateVariationFromInstruction(i)}>Create VO</button>
            )}
          />
        ))
      )}
      {tab==="form"&&(
        <div className="pm-form">
          {editingInstructionId&&<button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={()=>{clearInstructionForm();setTab("register");}}>← Cancel</button>}
          <Field label="SI Number"><input readOnly value={editingInstructionId?(instructionForm.siNumber||""):nextSiNumber}/></Field>
          <Field label="Date Issued"><input type="date" value={instructionForm.dateIssued||instructionForm.siDate||""} onChange={e=>upd("dateIssued",e.target.value)}/></Field>
          <Field label="Description"><textarea value={instructionForm.instructionDescription||""} onChange={e=>upd("instructionDescription",e.target.value)}/></Field>
          <Field label="Issued To"><input value={instructionForm.issuedTo||""} onChange={e=>upd("issuedTo",e.target.value)}/></Field>
          <Field label="Contract Clause"><input value={instructionForm.contractClause||""} onChange={e=>upd("contractClause",e.target.value)}/></Field>
          <Field label="Status">
            <select value={instructionForm.status||""} onChange={e=>upd("status",e.target.value)}>
              <option value="">Select</option>
              {["Outstanding","In Process","Completed","Cancelled"].map(s=><option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Creates Variation">
            <select value={instructionForm.createsVariation||"No"} onChange={e=>upd("createsVariation",e.target.value)}>
              <option>No</option><option>Yes</option>
            </select>
          </Field>
          <Field label="Linked RFI">
            <select value={instructionForm.relatedRfiId||""} onChange={e=>upd("relatedRfiId",e.target.value)}>
              <option value="">Not linked</option>
              {projRfis.map(r=><option key={r.id} value={r.id}>{r.rfiNo} — {r.subject}</option>)}
            </select>
          </Field>
          <Field label="Notes"><textarea value={instructionForm.notes||""} onChange={e=>upd("notes",e.target.value)}/></Field>
          <button className="pm-btn pm-btn--primary pm-btn--full" onClick={()=>{handleAddInstruction();setTab("register");}}>
            {editingInstructionId?"Update SI":"Save SI"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── DIARY ─────────────────────────────────────────────────────────────────────
function DiaryPanel({diaries=[],currentProjectId,diaryForm,setDiaryForm,handleAddDiary,handleEditDiary,handleDeleteDiary,editingDiaryId,clearDiaryForm,handleCopyPreviousDiary,handleViewItem}){
  const [tab,setTab]=useState("register");
  const [search,setSearch]=useState("");
  const [filter,setFilter]=useState("all");
  const [sortDir,setSortDir]=useState("desc");
  const rows=diaries.filter(d=>d.projectId===currentProjectId);
  // The user only selects the date; the diary number is generated from it as SD-YYYY-MM-DD.
  const upd=(k,v)=>{
    if(k==="date"){setDiaryForm({...diaryForm,date:v,diaryNo:v?`SD-${v}`:"",projectId:currentProjectId});return;}
    setDiaryForm({...diaryForm,[k]:v,projectId:currentProjectId});
  };
  const updFlag=(g,k,v)=>setDiaryForm({...diaryForm,projectId:currentProjectId,[g]:{...(diaryForm[g]||{}),[k]:v}});
  const labourItems=["foreman","supervisor","driver","operator","artisan","labourer"];
  const plantItems=["tlb","excavator","tipperTruck","waterCart","roller","generator"];
  const filters=[
    {key:"all",label:"All"},
    {key:"open",label:"Open"},
    {key:"locked",label:"Locked"},
    {key:"signed",label:"Fully Signed"},
    {key:"unsigned",label:"Unsigned"},
  ];
  const matchesFilter=d=>{
    if(filter==="open")return d.isLocked!=="Yes";
    if(filter==="locked")return d.isLocked==="Yes";
    if(filter==="signed")return d.contractorSigned==="Yes"&&d.engineerSigned==="Yes";
    if(filter==="unsigned")return d.contractorSigned!=="Yes"||d.engineerSigned!=="Yes";
    return true;
  };
  const q=search.trim().toLowerCase();
  const visibleRows=rows
    .filter(matchesFilter)
    .filter(d=>!q||[d.diaryNo,d.date,d.workSection,d.worksPerformed,d.generalComments,d.staffSummary,d.plantSummary,d.contractorSignName,d.engineerSignName]
      .map(v=>String(v||"").toLowerCase()).join(" ").includes(q))
    .sort((a,b)=>{
      const cmp=String(a.date||"").localeCompare(String(b.date||""));
      return sortDir==="desc"?-cmp:cmp;
    });
  return(
    <div className="pm-panel">
      <TabBar tabs={[{key:"register",label:"Register",count:rows.length},{key:"form",label:editingDiaryId?"Edit Entry":"+ New Entry"}]} active={tab} onChange={setTab}/>
      {tab==="register"&&(
        rows.length===0?<EmptyState icon="✎" title="No diary entries"/>:
        <>
          <input className="pm-search" placeholder="Search diary no, date, section, works…" value={search} onChange={e=>setSearch(e.target.value)}/>
          <div className="pm-filter-row">
            {filters.map(f=>(
              <button key={f.key} className={`pm-chip${filter===f.key?" active":""}`} onClick={()=>setFilter(f.key)}>{f.label}</button>
            ))}
            <button className="pm-chip" onClick={()=>setSortDir(d=>d==="desc"?"asc":"desc")}>
              {sortDir==="desc"?"Date ↓ Newest first":"Date ↑ Oldest first"}
            </button>
          </div>
          <div className="pm-audit-count">{visibleRows.length} of {rows.length} {rows.length===1?"diary":"diaries"}</div>
          {visibleRows.length===0?<EmptyState icon="✎" title="No matching diaries"/>:
          visibleRows.map(d=>(
            <RegItem key={d.id}
              primary={`Diary ${d.diaryNo||d.id}`} secondary={d.date}
              tag={d.isLocked==="Yes"?"Locked":"Open"} tagColor={d.isLocked==="Yes"?"#34d399":"#fbbf24"}
              meta={[
                {label:"Work Section", value:d.workSection},
                {label:"Rainfall mm",  value:d.rainfallMm||"0"},
                {label:"Contractor",   value:d.contractorSigned||"No"},
                {label:"Engineer",     value:d.engineerSigned||"No"},
              ]}
              onView={handleViewItem?()=>handleViewItem(`Diary ${d.diaryNo}`,d):null}
              onEdit={()=>{handleEditDiary(d);setTab("form");}}
              onDelete={()=>handleDeleteDiary(d.id)}
            />
          ))}
        </>
      )}
      {tab==="form"&&(
        <div className="pm-form">
          <div className="pm-form__actions">
            <button className="pm-btn pm-btn--green pm-btn--sm" onClick={handleCopyPreviousDiary}>Copy Prev Day</button>
            {editingDiaryId&&<button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={()=>{clearDiaryForm();setTab("register");}}>← Cancel</button>}
          </div>
          <Field label="Date" helper="Select the date — the diary number is generated automatically"><input type="date" value={diaryForm.date||""} onChange={e=>upd("date",e.target.value)}/></Field>
          <Field label="Diary Number"><input value={diaryForm.diaryNo||""} readOnly placeholder="Select a date above"/></Field>
          <Field label="Rainfall (mm)"><input type="number" value={diaryForm.rainfallMm||""} onChange={e=>upd("rainfallMm",e.target.value)}/></Field>
          <Field label="Work Section"><input value={diaryForm.workSection||""} onChange={e=>upd("workSection",e.target.value)}/></Field>
          <FormDivider label="Labour"/>
          <div className="pm-checkbox-grid">
            {labourItems.map(k=>(
              <label key={k} className="pm-checkbox">
                <input type="checkbox" checked={!!((diaryForm.labourFlags||{})[k])} onChange={e=>updFlag("labourFlags",k,e.target.checked)}/>
                <span>{k.charAt(0).toUpperCase()+k.slice(1)}</span>
              </label>
            ))}
          </div>
          <Field label="Staff Summary"><textarea value={diaryForm.staffSummary||""} onChange={e=>upd("staffSummary",e.target.value)}/></Field>
          <FormDivider label="Plant & Equipment"/>
          <div className="pm-checkbox-grid">
            {plantItems.map(k=>(
              <label key={k} className="pm-checkbox">
                <input type="checkbox" checked={!!((diaryForm.plantFlags||{})[k])} onChange={e=>updFlag("plantFlags",k,e.target.checked)}/>
                <span>{k.charAt(0).toUpperCase()+k.slice(1)}</span>
              </label>
            ))}
          </div>
          <Field label="Plant Summary"><textarea value={diaryForm.plantSummary||""} onChange={e=>upd("plantSummary",e.target.value)}/></Field>
          <FormDivider label="Works & Comments"/>
          <Field label="Works Performed"><textarea value={diaryForm.worksPerformed||""} onChange={e=>upd("worksPerformed",e.target.value)}/></Field>
          <Field label="General Comments"><textarea value={diaryForm.generalComments||""} onChange={e=>upd("generalComments",e.target.value)}/></Field>
          <FormDivider label="Sign Off"/>
          <Field label="Contractor Rep"><input value={diaryForm.contractorSignName||""} onChange={e=>upd("contractorSignName",e.target.value)} disabled={diaryForm.isLocked==="Yes"}/></Field>
          <Field label="Contractor Signed">
            <select value={diaryForm.contractorSigned||"No"} onChange={e=>upd("contractorSigned",e.target.value)} disabled={diaryForm.isLocked==="Yes"}>
              <option value="No">Not signed</option><option value="Yes">Signed</option>
            </select>
          </Field>
          <Field label="Engineer Rep"><input value={diaryForm.engineerSignName||""} onChange={e=>upd("engineerSignName",e.target.value)} disabled={diaryForm.isLocked==="Yes"}/></Field>
          <Field label="Engineer Signed">
            <select value={diaryForm.engineerSigned||"No"} onChange={e=>upd("engineerSigned",e.target.value)} disabled={diaryForm.isLocked==="Yes"}>
              <option value="No">Not signed</option><option value="Yes">Signed</option>
            </select>
          </Field>
          <button className="pm-btn pm-btn--primary pm-btn--full" onClick={()=>{handleAddDiary();setTab("register");}}>
            {editingDiaryId?"Update Diary":"Save Diary"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── NCR ───────────────────────────────────────────────────────────────────────
function NCRPanel({ncrs=[],currentProjectId,ncrForm,setNcrForm,handleAddOrUpdateNcr,handleEditNcr,handleDeleteNcr,editingNcrId,clearNcrForm,handleViewItem,variations=[]}){
  const [tab,setTab]=useState("register");
  const [filter,setFilter]=useState("All");
  const rows=ncrs.filter(n=>n.projectId===currentProjectId).filter(n=>filter==="All"||n.status===filter);
  const upd=(k,v)=>setNcrForm({...ncrForm,[k]:v,projectId:currentProjectId});
  return(
    <div className="pm-panel">
      <div className="pm-grid-2">
        <StatChip label="Open"     value={String(ncrs.filter(n=>n.projectId===currentProjectId&&n.status==="Open").length)}     accent="#f87171"/>
        <StatChip label="Resolved" value={String(ncrs.filter(n=>n.projectId===currentProjectId&&n.status==="Resolved").length)} accent="#34d399"/>
      </div>
      <TabBar tabs={[{key:"register",label:"Register",count:rows.length},{key:"form",label:editingNcrId?"Edit NCR":"+ New NCR"}]} active={tab} onChange={setTab}/>
      {tab==="register"&&<>
        <FilterChips options={["All","Open","Resolved"]} active={filter} onChange={setFilter}/>
        {rows.length===0?<EmptyState icon="⚑" title="No NCRs"/>:
          rows.map(n=>(
            <RegItem key={n.id}
              primary={n.ncrNo||n.id} secondary={n.description||""}
              tag={n.status||"Open"} tagColor={n.status==="Resolved"?"#34d399":"#f87171"}
              meta={[
                {label:"Category",  value:n.category||"—"},
                {label:"Severity",  value:n.severity||"—"},
                {label:"Location",  value:n.location||"—"},
                {label:"Issued",    value:n.issueDate||"—",mono:true},
                {label:"Close By",  value:n.requiredCloseOutDate||"—",mono:true},
                {label:"Contractor",value:n.contractor||"—"},
              ]}
              onView={handleViewItem?()=>handleViewItem(`NCR ${n.ncrNo}`,n):null}
              onEdit={()=>{handleEditNcr(n);setTab("form");}}
              onDelete={()=>handleDeleteNcr(n.id)}
            />
          ))
        }
      </>}
      {tab==="form"&&(
        <div className="pm-form">
          {editingNcrId&&<button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={()=>{clearNcrForm();setTab("register");}}>← Cancel</button>}
          <Field label="NCR Number"><input value={ncrForm.ncrNo||""} onChange={e=>upd("ncrNo",e.target.value)}/></Field>
          <Field label="Issue Date"><input type="date" value={ncrForm.issueDate||""} onChange={e=>upd("issueDate",e.target.value)}/></Field>
          <Field label="Contractor"><input value={ncrForm.contractor||""} onChange={e=>upd("contractor",e.target.value)}/></Field>
          <Field label="Description"><textarea value={ncrForm.description||""} onChange={e=>upd("description",e.target.value)}/></Field>
          <Field label="Category">
            <select value={ncrForm.category||"Workmanship"} onChange={e=>upd("category",e.target.value)}>
              {["Workmanship","Materials","Design","Documentation","Safety","Environmental"].map(s=><option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Severity">
            <select value={ncrForm.severity||"Medium"} onChange={e=>upd("severity",e.target.value)}>
              {["Low","Medium","High","Critical"].map(s=><option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={ncrForm.status||"Open"} onChange={e=>upd("status",e.target.value)}>
              <option>Open</option><option>Resolved</option>
            </select>
          </Field>
          <Field label="Location"><input value={ncrForm.location||""} onChange={e=>upd("location",e.target.value)}/></Field>
          <Field label="Spec Reference"><input value={ncrForm.specificationReference||""} onChange={e=>upd("specificationReference",e.target.value)}/></Field>
          <Field label="Required Close Out Date"><input type="date" value={ncrForm.requiredCloseOutDate||""} onChange={e=>upd("requiredCloseOutDate",e.target.value)}/></Field>
          <Field label="Date Resolved"><input type="date" value={ncrForm.dateResolved||""} onChange={e=>upd("dateResolved",e.target.value)}/></Field>
          <Field label="Corrective Action"><textarea value={ncrForm.correctiveAction||""} onChange={e=>upd("correctiveAction",e.target.value)}/></Field>
          <Field label="Root Cause"><textarea value={ncrForm.rootCause||""} onChange={e=>upd("rootCause",e.target.value)}/></Field>
          <button className="pm-btn pm-btn--primary pm-btn--full" onClick={()=>{handleAddOrUpdateNcr();setTab("register");}}>
            {editingNcrId?"Update NCR":"Save NCR"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── RISK ──────────────────────────────────────────────────────────────────────
function RiskPanel({currentProjectId}){
  const [risks,setRisks]=useState([]);
  const [tab,setTab]=useState("register");
  const [filter,setFilter]=useState("All");
  const [editing,setEditing]=useState(null);
  const emptyForm={type:"",author:"",dateRaised:"",riskEvent:"",impactDescription:"",likelihood:1,severity:1,riskOwner:"",riskResponseType:"Prevention",contingencyMeasure:"",dueDate:"",status:"Logged",progressNotes:""};
  const [form,setForm]=useState(emptyForm);
  useEffect(()=>{
    if(!currentProjectId) return;
    return onSnapshot(query(collection(db,"risks"),orderBy("createdAt","desc")),
      snap=>setRisks(snap.docs.map(d=>({id:d.id,...d.data()})).filter(r=>r.projectId===currentProjectId)));
  },[currentProjectId]);
  const getRating=(l,s)=>{ const sc=(n0(l)+n0(s))/2; if(n0(l)>=4&&n0(s)>=4) return "high"; if(n0(l)<=2&&n0(s)<=2) return "low"; return sc>=4?"high":sc>=3?"medium":"low"; };
  const ratingColor={low:"#34d399",medium:"#fbbf24",high:"#f87171"};
  const rows=risks.filter(r=>filter==="All"||r.status===filter);
  const upd=(k,v)=>setForm({...form,[k]:v});
  const handleSave=async()=>{
    const payload={...form,projectId:currentProjectId,updatedAt:serverTimestamp()};
    if(editing){await updateDoc(doc(db,"risks",editing),payload);}
    else{await addDoc(collection(db,"risks"),{...payload,createdAt:serverTimestamp()});}
    setEditing(null);setForm(emptyForm);setTab("register");
  };
  const STATUSES=["Logged","Analysis","Decision","Allocated","Action","Escalated","Closed","Occurred"];
  const TYPES=["Climate","Geological","Biological","ET","Social","Political","Environmental","Financial","Cultural","Technical","Health","Managerial","Procure","Crime","Legal"];
  const RESPONSES=["Prevention","Reduction","Transference","Contingency","Acceptance"];
  return(
    <div className="pm-panel">
      <div className="pm-grid-2">
        <StatChip label="Total"  value={String(risks.length)} accent="#f59e0b"/>
        <StatChip label="High"   value={String(risks.filter(r=>getRating(r.likelihood,r.severity)==="high").length)} accent="#f87171"/>
        <StatChip label="Open"   value={String(risks.filter(r=>!["Closed","Occurred"].includes(r.status||"")).length)} accent="#fbbf24"/>
        <StatChip label="Closed" value={String(risks.filter(r=>r.status==="Closed").length)} accent="#34d399"/>
      </div>
      <TabBar tabs={[{key:"register",label:"Register",count:rows.length},{key:"form",label:editing?"Edit Risk":"+ New Risk"}]} active={tab} onChange={setTab}/>
      {tab==="register"&&<>
        <FilterChips options={["All",...STATUSES]} active={filter} onChange={setFilter}/>
        {rows.length===0?<EmptyState icon="⚠" title="No risks logged"/>:
          rows.map(r=>{
            const rat=getRating(r.likelihood,r.severity);
            return(
              <RegItem key={r.id}
                primary={r.riskEvent||r.id} secondary={r.type||""}
                tag={r.status||"Logged"} tagColor={ratingColor[rat]}
                meta={[
                  {label:"Rating",    value:rat.toUpperCase()},
                  {label:"Likelihood",value:String(r.likelihood||1)},
                  {label:"Severity",  value:String(r.severity||1)},
                  {label:"Owner",     value:r.riskOwner||"—"},
                  {label:"Response",  value:r.riskResponseType||"—"},
                  {label:"Due",       value:r.dueDate||"—",mono:true},
                ]}
                onEdit={()=>{setForm({...r});setEditing(r.id);setTab("form");}}
                onDelete={async()=>await deleteDoc(doc(db,"risks",r.id))}
              />
            );
          })
        }
      </>}
      {tab==="form"&&(
        <div className="pm-form">
          {editing&&<button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={()=>{setEditing(null);setTab("register");}}>← Cancel</button>}
          <Field label="Risk Type">
            <select value={form.type||""} onChange={e=>upd("type",e.target.value)}>
              <option value="">Select…</option>
              {TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Risk Event"><textarea value={form.riskEvent||""} onChange={e=>upd("riskEvent",e.target.value)}/></Field>
          <Field label="Impact Description"><textarea value={form.impactDescription||""} onChange={e=>upd("impactDescription",e.target.value)}/></Field>
          <Field label="Author"><input value={form.author||""} onChange={e=>upd("author",e.target.value)}/></Field>
          <Field label="Date Raised"><input type="date" value={form.dateRaised||""} onChange={e=>upd("dateRaised",e.target.value)}/></Field>
          <Field label="Likelihood (1–5)"><input type="number" min="1" max="5" value={form.likelihood||1} onChange={e=>upd("likelihood",e.target.value)}/></Field>
          <Field label="Severity (1–5)"><input type="number" min="1" max="5" value={form.severity||1} onChange={e=>upd("severity",e.target.value)}/></Field>
          <Field label="Risk Owner"><input value={form.riskOwner||""} onChange={e=>upd("riskOwner",e.target.value)}/></Field>
          <Field label="Response Type">
            <select value={form.riskResponseType||"Prevention"} onChange={e=>upd("riskResponseType",e.target.value)}>
              {RESPONSES.map(r=><option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Contingency Measure"><textarea value={form.contingencyMeasure||""} onChange={e=>upd("contingencyMeasure",e.target.value)}/></Field>
          <Field label="Due Date"><input type="date" value={form.dueDate||""} onChange={e=>upd("dueDate",e.target.value)}/></Field>
          <Field label="Status">
            <select value={form.status||"Logged"} onChange={e=>upd("status",e.target.value)}>
              {STATUSES.map(s=><option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Progress Notes"><textarea value={form.progressNotes||""} onChange={e=>upd("progressNotes",e.target.value)}/></Field>
          <button className="pm-btn pm-btn--primary pm-btn--full" onClick={handleSave}>
            {editing?"Update Risk":"Save Risk"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── AUDIT ─────────────────────────────────────────────────────────────────────
function AuditPanel({auditLogs=[]}){
  const [search,setSearch]=useState("");
  const filtered=auditLogs
    .filter(l=>!search||JSON.stringify(l).toLowerCase().includes(search.toLowerCase()))
    .sort((a,b)=>{
      const at=typeof a?.createdAt?.toDate==="function"?a.createdAt.toDate().getTime():new Date(a?.createdAt||0).getTime();
      const bt=typeof b?.createdAt?.toDate==="function"?b.createdAt.toDate().getTime():new Date(b?.createdAt||0).getTime();
      return bt-at;
    });
  const actionColor={delete:"#f87171",create:"#34d399",update:"#60a5fa",status_change:"#60a5fa"};
  return(
    <div className="pm-panel">
      <input className="pm-search" placeholder="Search logs…" value={search} onChange={e=>setSearch(e.target.value)}/>
      <div className="pm-audit-count">{filtered.length} log{filtered.length!==1?"s":""}</div>
      {filtered.length===0?<EmptyState icon="≋" title="No audit logs"/>:
        filtered.map((l,i)=>(
          <div key={l.id||i} className="pm-audit-item">
            <div className="pm-audit-item__head">
              <span className="pm-audit-item__action" style={{color:actionColor[l.action]||"#94a3b8"}}>{l.action||"—"}</span>
              <span className="pm-audit-item__entity">{l.entityType||"—"}</span>
              <span className="pm-audit-item__time">{l.createdAt?(typeof l.createdAt.toDate==="function"?l.createdAt.toDate():new Date(l.createdAt)).toLocaleString():"—"}</span>
            </div>
            <div className="pm-audit-item__summary">{l.summary||l.message||"—"}</div>
            <div className="pm-audit-item__user">{l.userName||l.userEmail||"—"}</div>
          </div>
        ))
      }
    </div>
  );
}

// ─── PROJECT TRACKER ───────────────────────────────────────────────────────────
function TrackerPanel({currentProject,rfis=[],variations=[],claims=[],currentProjectId}){
  const projRfis=rfis.filter(r=>r.projectId===currentProjectId);
  const projVars=variations.filter(v=>v.projectId===currentProjectId);
  const projClaims=claims.filter(c=>c.projectId===currentProjectId);
  const openRfis=projRfis.filter(r=>r.status==="Open"||r.status==="Under Review");
  const pendingVos=projVars.filter(v=>!["Approved","Rejected"].includes(v.status||""));
  const activeClaims=projClaims.filter(c=>c.status==="In Review");
  return(
    <div className="pm-panel">
      <div className="pm-grid-2">
        <StatChip label="Open RFIs"     value={String(openRfis.length)}     accent="#f87171"/>
        <StatChip label="Pending VOs"   value={String(pendingVos.length)}   accent="#fbbf24"/>
        <StatChip label="Active Claims" value={String(activeClaims.length)} accent="#fb923c"/>
        <StatChip label="Total VOs"     value={String(projVars.length)}     accent="#60a5fa"/>
      </div>
      <SCard title="Open RFIs" pad={false}>
        {openRfis.length===0?<div style={{padding:"12px 14px",color:"var(--pm-text3)",fontSize:12}}>All RFIs closed ✓</div>:
          openRfis.slice(0,8).map(r=><DataRow key={r.id} label={`RFI ${r.rfiNo||r.id}`} value={r.subject||r.status||"—"}/>)
        }
      </SCard>
      <SCard title="Pending VOs" pad={false}>
        {pendingVos.length===0?<div style={{padding:"12px 14px",color:"var(--pm-text3)",fontSize:12}}>No pending VOs ✓</div>:
          pendingVos.slice(0,8).map(v=><DataRow key={v.id} label={`VO ${v.variationNo||v.id}`} value={`${v.status||"—"} — ${fmtC(v.amount||0)}`} mono/>)
        }
      </SCard>
      <SCard title="Claims In Review" pad={false}>
        {activeClaims.length===0?<div style={{padding:"12px 14px",color:"var(--pm-text3)",fontSize:12}}>No active claims ✓</div>:
          activeClaims.slice(0,8).map(c=><DataRow key={c.id} label={`Claim ${c.claimNo||c.id}`} value={c.title||"—"}/>)
        }
      </SCard>
    </div>
  );
}

// ─── USERS ─────────────────────────────────────────────────────────────────────
function UsersPanel(){
  const [users,setUsers]=useState([]);
  useEffect(()=>onSnapshot(collection(db,"users"),snap=>setUsers(snap.docs.map(d=>({id:d.id,...d.data()})))),[]);
  const roleColor={administrator:"#e879f9",engineer:"#60a5fa",site_engineer:"#34d399",client:"#fbbf24"};
  return(
    <div className="pm-panel">
      <StatChip label="Active Users" value={String(users.length)} accent="#f59e0b"/>
      <SCard title="User Register" pad={false}>
        {users.length===0?<EmptyState icon="⊛" title="No users assigned"/>:
          users.map(u=>(
            <div key={u.id} className="pm-data-row" style={{alignItems:"center",minHeight:52}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"var(--pm-text)"}}>{u.displayName||"—"}</div>
                <div style={{fontSize:11,color:"var(--pm-text3)"}}>{u.email||"—"}</div>
              </div>
              <span className="pm-reg-item__tag" style={{"--tag-color":roleColor[u.role]||"#f59e0b"}}>{u.role||"—"}</span>
            </div>
          ))
        }
      </SCard>
      <EmptyState sub="To add or edit users, open User Management on a desktop browser."/>
    </div>
  );
}

// ─── Simpler read-only panels ──────────────────────────────────────────────────
function CashflowPanel({currentProjectId,certificates=[]}){
  const certs=certificates.filter(c=>c.projectId===currentProjectId).sort((a,b)=>String(a.certificateMonth||"").localeCompare(String(b.certificateMonth||"")));
  const total=certs.reduce((s,c)=>s+n0(c.subtotalA||c.totalCertified||0),0);
  return(
    <div className="pm-panel">
      <StatChip label="Total Certified" value={fmt(total)} accent="#34d399"/>
      <SCard title="Certificate History" pad={false}>
        {certs.length===0?<EmptyState icon="⟁" title="No cashflow data" sub="Certificates drive the cashflow view."/>:
          certs.map(c=><DataRow key={c.id} label={c.certificateMonth||c.certificateNo||c.id} value={fmt(c.subtotalA||c.totalCertified||0)} mono accent/>)
        }
      </SCard>
    </div>
  );
}

function ProgrammePanel({currentProject}){
  if(!currentProject) return <EmptyState icon="▦" title="No project"/>;
  const prog=Math.min(currentProject.progress||0,100);
  return(
    <div className="pm-panel">
      <SCard title="Programme Summary" pad={false}>
        <DataRow label="Start Date"  value={currentProject.startDate||"—"} mono/>
        <DataRow label="Planned End" value={currentProject.estimatedEndDate||currentProject.completionDate||"—"} mono/>
        <DataRow label="Revised End" value={currentProject.revisedEndDate||"—"} mono/>
        <DataRow label="Progress"    value={`${prog}%`} accent/>
      </SCard>
      <SCard title="Progress">
        <div className="pm-progress" style={{paddingBottom:8}}>
          <div className="pm-progress__track" style={{height:10}}><div className="pm-progress__fill" style={{width:`${prog}%`}}/></div>
          <span className="pm-progress__label">{prog}%</span>
        </div>
      </SCard>
      <EmptyState icon="▦" sub="Full Gantt programme available on desktop."/>
    </div>
  );
}

function BOQPanel({currentProjectId,variations=[],soqItems=[]}){
  return(
    <div className="pm-panel">
      <div className="pm-grid-2">
        <StatChip label="SOQ Items" value={String(soqItems.filter(s=>s.projectId===currentProjectId).length)} accent="#34d399"/>
        <StatChip label="Total VOs"  value={String(variations.filter(v=>v.projectId===currentProjectId).length)} accent="#f59e0b"/>
      </div>
      <EmptyState icon="⊞" sub="Full BOQ register available on desktop."/>
    </div>
  );
}

function ContractPanel({currentProject}){
  if(!currentProject) return <EmptyState icon="⊡" title="No project"/>;
  return(
    <div className="pm-panel">
      <SCard title="Contract Summary" pad={false}>
        <DataRow label="Contract No."  value={currentProject.contractNumber}      mono/>
        <DataRow label="Type"          value={currentProject.contractType}/>
        <DataRow label="Description"   value={currentProject.contractDescription}/>
        <DataRow label="Contractor"    value={currentProject.contractor}/>
        <DataRow label="Client"        value={currentProject.client}/>
        <DataRow label="Department"    value={currentProject.department}/>
      </SCard>
      <EmptyState icon="⊡" sub="Full contract document management available on desktop."/>
    </div>
  );
}

function SOQRegisterPanel({currentProjectId,soqItems=[]}){
  const items=soqItems.filter(s=>s.projectId===currentProjectId);
  return(
    <div className="pm-panel">
      <StatChip label="SOQ Items" value={String(items.length)} accent="#34d399"/>
      <SCard title="SOQ Register" pad={false}>
        {items.length===0?<EmptyState icon="≡" title="No SOQ items"/>:
          items.slice(0,20).map(i=><DataRow key={i.id} label={i.itemNo||i.id} value={i.description||"—"}/>)
        }
        {items.length>20&&<div style={{padding:"8px 14px",fontSize:11,color:"var(--pm-text3)"}}>+{items.length-20} more on desktop</div>}
      </SCard>
    </div>
  );
}

const SimplePanel=({icon,title,sub})=><div className="pm-panel"><EmptyState icon={icon} title={title} sub={sub||"Full access available on desktop."}/></div>;

// ─── Drawers & Modals ──────────────────────────────────────────────────────────
function SectionsDrawer({open,onClose,activeSection,onNavigate,isAdmin}){
  // Hide admin-only sections (Users) from non-admins, then drop empty groups.
  const groups = isAdmin
    ? GROUPS
    : GROUPS
        .map(g=>({...g,sections:g.sections.filter(s=>!s.adminOnly)}))
        .filter(g=>g.sections.length>0);
  return(
    <>
      {open&&<div className="pm-drawer-backdrop" onClick={onClose}/>}
      <div className={`pm-drawer${open?" open":""}`} role="dialog" aria-modal="true">
        <div className="pm-drawer__header">
          <span className="pm-drawer__title">Navigate</span>
          <button className="pm-drawer__close" onClick={onClose}>✕</button>
        </div>
        <div className="pm-drawer__body">
          {groups.map(g=>(
            <div key={g.id} className="pm-drawer__group">
              <div className="pm-drawer__group-label" style={{color:g.color}}>
                <span className="pm-drawer__group-dot" style={{background:g.color}}/>{g.label}
              </div>
              <div className="pm-drawer__items">
                {g.sections.map(s=>(
                  <button key={s.key}
                    className={`pm-drawer__item${activeSection===s.key?" active":""}`}
                    style={activeSection===s.key?{borderColor:g.color+"80",background:g.color+"18",color:"#fff"}:{}}
                    onClick={()=>{onNavigate(s.key);onClose();}}>
                    <span className="pm-drawer__item-icon">{s.icon}</span>{s.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{height:80}}/>
      </div>
    </>
  );
}

function ProjectModal({open,onClose,projects,selectedProject,setSelectedProject}){
  if(!open) return null;
  return(
    <div className="pm-modal-backdrop" onClick={onClose}>
      <div className="pm-modal" onClick={e=>e.stopPropagation()}>
        <div className="pm-modal__header">
          <span className="pm-modal__title">Select Project</span>
          <button className="pm-drawer__close" onClick={onClose}>✕</button>
        </div>
        <div className="pm-modal__body">
          {(projects||[]).map(p=>(
            <button key={p.id} className={`pm-project-option${selectedProject===p.id?" active":""}`}
              onClick={()=>{setSelectedProject(p.id);onClose();}}>
              <span className="pm-project-option__dot"/>
              <div>
                <div className="pm-project-option__name">{p.name||p.id}</div>
                <div className="pm-project-option__id">{p.id}</div>
              </div>
              {selectedProject===p.id&&<span className="pm-project-option__check">✓</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Quality Tests — native mobile capture ──────────────────────────────────────
// Writes the identical schema to projects/{id}/qualityTests using the builders
// and status logic exported from QualityTestsSection, so captures made on the
// phone show up unchanged on desktop and the Visual Progress tracker.
const subcardStyle = { border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"10px 12px", margin:"8px 0", background:"rgba(255,255,255,0.02)" };
const subheadStyle = { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6, fontWeight:600, fontSize:13, color:"#cbd5e1" };

function ResultBadge({status}){
  const r=RESULT_COLORS[status]||RESULT_COLORS.pending;
  return <span className="pm-reg-item__tag" style={{"--tag-color":r.fill}}>{r.label}</span>;
}

function QualityPanel({currentProjectId}){
  const [tests,setTests]=useState([]);
  const [tab,setTab]=useState("register");
  const [filter,setFilter]=useState("All");
  const [form,setForm]=useState(()=>blankForm("cube"));
  const [editId,setEditId]=useState(null);
  const [saving,setSaving]=useState(false);

  useEffect(()=>{
    if(!currentProjectId){setTests([]);return;}
    const unsub=onSnapshot(collection(db,"projects",currentProjectId,"qualityTests"),
      snap=>setTests(snap.docs.map(d=>({id:d.id,...d.data()}))),
      ()=>setTests([]));
    return ()=>unsub();
  },[currentProjectId]);

  const set=patch=>setForm(f=>({...f,...patch}));
  const setSub=(key,patch)=>setForm(f=>({...f,[key]:{...f[key],...patch}}));
  const resetForm=(testType="cube")=>{setForm(blankForm(testType));setEditId(null);};
  const chooseType=testType=>setForm(f=>({...blankForm(testType),title:f.title,testDate:f.testDate,reportNo:f.reportNo,lab:f.lab,technician:f.technician,notes:f.notes}));
  function editTest(t){
    const base=blankForm(t.testType||"cube");
    setForm({...base,...t,
      compaction:{...base.compaction,...(t.compaction||{}),positions:(t.compaction?.positions?.length?t.compaction.positions:base.compaction.positions)},
      ndt:{...base.ndt,...(t.ndt||{})},
      cube:{...base.cube,...(t.cube||{}),cubes:(t.cube?.cubes?.length?t.cube.cubes:base.cube.cubes)},
      pressure:{...base.pressure,...(t.pressure||{})},
    });
    setEditId(t.id);setTab("capture");
  }

  const positions=form.compaction?.positions||[];
  const setPos=(i,patch)=>setForm(f=>({...f,compaction:{...f.compaction,positions:(f.compaction.positions||[]).map((p,idx)=>idx===i?{...p,...patch}:p)}}));
  const addPos=()=>setForm(f=>({...f,compaction:{...f.compaction,positions:[...(f.compaction.positions||[]),emptyPosition(f.compaction.positions||[],"")]}}));
  const rmPos=i=>setForm(f=>({...f,compaction:{...f.compaction,positions:(f.compaction.positions||[]).filter((_,idx)=>idx!==i)}}));

  const cubes=form.cube?.cubes||[];
  const setCube=(i,patch)=>setForm(f=>({...f,cube:{...f.cube,cubes:(f.cube.cubes||[]).map((c,idx)=>idx===i?{...c,...patch}:c)}}));
  const addCube=lbl=>setForm(f=>({...f,cube:{...f.cube,cubes:[...(f.cube.cubes||[]),emptyCube(lbl||"28")]}}));
  const rmCube=i=>setForm(f=>({...f,cube:{...f.cube,cubes:(f.cube.cubes||[]).filter((_,idx)=>idx!==i)}}));

  async function save(){
    if(!currentProjectId)return;
    setSaving(true);
    try{
      const status=computeTestStatus(form);
      const data={...form,projectId:currentProjectId,status,updatedAt:serverTimestamp()};
      if(editId){
        await setDoc(doc(db,"projects",currentProjectId,"qualityTests",editId),data,{merge:true});
      }else{
        await addDoc(collection(db,"projects",currentProjectId,"qualityTests"),{...data,createdAt:serverTimestamp()});
      }
      resetForm(form.testType);setTab("register");
    }finally{setSaving(false);}
  }
  async function del(t){
    if(!currentProjectId)return;
    if(!window.confirm("Delete this test? This cannot be undone."))return;
    await deleteDoc(doc(db,"projects",currentProjectId,"qualityTests",t.id));
  }

  const meta=TEST_TYPE_META[form.testType]||{};
  const mode=meta.locationMode;
  const liveStatus=computeTestStatus(form);
  const filtered=tests.filter(t=>filter==="All"?true:TEST_TYPE_META[t.testType]?.label===filter);

  if(!currentProjectId) return <EmptyState icon="⚗" title="No project selected" sub="Choose a project from the top bar."/>;

  return(
    <div>
      <TabBar tabs={[{key:"register",label:"Register",count:tests.length},{key:"capture",label:editId?"Edit":"Capture"}]} active={tab} onChange={setTab}/>

      {tab==="register"&&(
        <>
          <FilterChips options={["All",...TEST_TYPES.map(t=>t.label)]} active={filter} onChange={setFilter}/>
          <button className="pm-btn pm-btn--primary pm-btn--full" onClick={()=>{resetForm("cube");setTab("capture");}}>+ Capture Test</button>
          {filtered.length===0?<EmptyState icon="⚗" title="No tests" sub="Capture concrete cube, compaction, NDT and pressure tests here."/>:
            filtered.map(t=>{
              const m=TEST_TYPE_META[t.testType]||{};
              const r=RESULT_COLORS[t.status]||RESULT_COLORS.pending;
              return(
                <RegItem key={t.id}
                  primary={`${m.icon||"⚗"} ${t.title||m.label||t.testType}`}
                  secondary={testLocationLabel(t)}
                  tag={r.label} tagColor={r.fill}
                  meta={[
                    {label:"Type",value:m.label||t.testType},
                    {label:"Date",value:t.testDate||"—"},
                    {label:"Report No",value:t.reportNo||"—",mono:true},
                    {label:"Lab",value:t.lab||"—"},
                    {label:"Location",value:testLocationLabel(t)},
                  ]}
                  onEdit={()=>editTest(t)} onDelete={()=>del(t)}/>
              );
            })}
        </>
      )}

      {tab==="capture"&&(
        <div className="pm-form">
          {editId&&<button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={()=>{resetForm(form.testType);setTab("register");}}>← Cancel edit</button>}

          <Field label="Test Type">
            <div className="pm-filter-row">
              {TEST_TYPES.map(t=>(
                <button key={t.key} type="button" className={`pm-chip${form.testType===t.key?" active":""}`} onClick={()=>chooseType(t.key)}>{t.icon} {t.label}</button>
              ))}
            </div>
          </Field>

          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",margin:"2px 0 6px"}}>
            <span className="pm-field__label">Live result</span>
            <ResultBadge status={liveStatus}/>
          </div>

          <Field label="Reference / Title" helper="Shown on the register; used for duplicate detection"><input value={form.title||""} onChange={e=>set({title:e.target.value})} placeholder="e.g. FW87 INT"/></Field>
          <Field label="Test Date"><input type="date" value={form.testDate||""} onChange={e=>set({testDate:e.target.value})}/></Field>
          <Field label="Report No"><input value={form.reportNo||""} onChange={e=>set({reportNo:e.target.value})}/></Field>
          <Field label="Lab"><input value={form.lab||""} onChange={e=>set({lab:e.target.value})}/></Field>
          <Field label="Technician"><input value={form.technician||""} onChange={e=>set({technician:e.target.value})}/></Field>

          {mode==="range"&&(<>
            <div className="pm-form__divider">Location</div>
            <Field label="Chainage Start"><input type="number" inputMode="decimal" value={form.chainageStart||""} onChange={e=>set({chainageStart:e.target.value})}/></Field>
            <Field label="Chainage End"><input type="number" inputMode="decimal" value={form.chainageEnd||""} onChange={e=>set({chainageEnd:e.target.value})}/></Field>
            <Field label="Side"><input value={form.side||""} onChange={e=>set({side:e.target.value})} placeholder="LHS / RHS / C/L"/></Field>
          </>)}
          {mode==="point"&&(<>
            <div className="pm-form__divider">Location</div>
            <Field label="Structure / Weld Ref"><input value={form.structureRef||""} onChange={e=>set({structureRef:e.target.value})}/></Field>
            <Field label="Chainage"><input type="number" inputMode="decimal" value={form.chainageStart||""} onChange={e=>set({chainageStart:e.target.value})}/></Field>
          </>)}
          {mode==="asset"&&(<>
            <div className="pm-form__divider">Location</div>
            <Field label="Asset / Section"><input value={form.assetName||""} onChange={e=>set({assetName:e.target.value})}/></Field>
          </>)}

          {form.testType==="compaction"&&(<>
            <div className="pm-form__divider">Density Positions</div>
            {positions.map((p,i)=>(
              <div key={i} style={subcardStyle}>
                <div style={subheadStyle}><span>{p.testNo||`Position ${i+1}`}</span>{positions.length>1&&<button className="pm-btn pm-btn--red pm-btn--sm" onClick={()=>rmPos(i)}>Remove</button>}</div>
                <Field label="Layer">
                  <select value={p.layerKey||"bedding"} onChange={e=>{const lk=e.target.value;const def=(COMPACTION_LAYERS.find(l=>l.key===lk)||{}).defaultRequired;setPos(i,{layerKey:lk,requiredCompaction:def});}}>
                    {COMPACTION_LAYERS.map(l=><option key={l.key} value={l.key}>{l.label}</option>)}
                  </select>
                </Field>
                <Field label="Chainage"><input type="number" inputMode="decimal" value={p.chainage||""} onChange={e=>setPos(i,{chainage:e.target.value})}/></Field>
                <Field label="Required %"><input type="number" inputMode="decimal" value={p.requiredCompaction??""} onChange={e=>setPos(i,{requiredCompaction:e.target.value})}/></Field>
                <Field label="Dry Density"><input type="number" inputMode="decimal" value={p.dryDensity||""} onChange={e=>setPos(i,{dryDensity:e.target.value})}/></Field>
                <Field label="MDD"><input type="number" inputMode="decimal" value={p.mdd||""} onChange={e=>setPos(i,{mdd:e.target.value})}/></Field>
                <Field label="Relative Compaction %" helper="Optional — derived from dry density ÷ MDD if blank"><input type="number" inputMode="decimal" value={p.relativeCompaction||""} onChange={e=>setPos(i,{relativeCompaction:e.target.value})}/></Field>
              </div>
            ))}
            <button className="pm-btn pm-btn--ghost pm-btn--full" onClick={addPos}>+ Add position</button>
          </>)}

          {form.testType==="ndt"&&(<>
            <div className="pm-form__divider">NDT / Weld</div>
            <Field label="Method"><input value={form.ndt.method||""} onChange={e=>setSub("ndt",{method:e.target.value})} placeholder="RT / UT / MPI"/></Field>
            <Field label="Weld No"><input value={form.ndt.weldNo||""} onChange={e=>setSub("ndt",{weldNo:e.target.value})}/></Field>
            <Field label="Welder"><input value={form.ndt.welder||""} onChange={e=>setSub("ndt",{welder:e.target.value})}/></Field>
            <Field label="Pipe OD"><input value={form.ndt.pipeOD||""} onChange={e=>setSub("ndt",{pipeOD:e.target.value})}/></Field>
            <Field label="Acceptance Standard"><input value={form.ndt.acceptanceStandard||""} onChange={e=>setSub("ndt",{acceptanceStandard:e.target.value})}/></Field>
            <Field label="Result"><select value={form.ndt.result||""} onChange={e=>setSub("ndt",{result:e.target.value})}><option value="">Pending</option><option value="Accept">Accept</option><option value="Reject">Reject</option></select></Field>
            <Field label="Defects"><textarea value={form.ndt.defects||""} onChange={e=>setSub("ndt",{defects:e.target.value})}/></Field>
          </>)}

          {form.testType==="cube"&&(<>
            <div className="pm-form__divider">Concrete Cube</div>
            <Field label="Pour Ref"><input value={form.cube.pourRef||""} onChange={e=>setSub("cube",{pourRef:e.target.value})}/></Field>
            <Field label="Element"><input value={form.cube.element||""} onChange={e=>setSub("cube",{element:e.target.value})}/></Field>
            <Field label="Specified Strength (MPa)"><input type="number" inputMode="decimal" value={form.cube.specifiedStrength||""} onChange={e=>setSub("cube",{specifiedStrength:e.target.value})}/></Field>
            <Field label="Date Cast"><input type="date" value={form.cube.dateCast||""} onChange={e=>setSub("cube",{dateCast:e.target.value})}/></Field>
            <Field label="Supplier"><input value={form.cube.supplier||""} onChange={e=>setSub("cube",{supplier:e.target.value})}/></Field>
            {cubes.map((c,i)=>(
              <div key={i} style={subcardStyle}>
                <div style={subheadStyle}><span>Cube {i+1}</span>{cubes.length>1&&<button className="pm-btn pm-btn--red pm-btn--sm" onClick={()=>rmCube(i)}>Remove</button>}</div>
                <Field label="Set / Age"><select value={c.setLabel||"28"} onChange={e=>setCube(i,{setLabel:e.target.value,ageDays:e.target.value})}><option value="7">7-day</option><option value="28">28-day</option></select></Field>
                <Field label="Cube ID"><input value={c.cubeId||""} onChange={e=>setCube(i,{cubeId:e.target.value})}/></Field>
                <Field label="Strength (MPa)" helper="Or enter load below to derive"><input type="number" inputMode="decimal" value={c.strengthMPa||""} onChange={e=>setCube(i,{strengthMPa:e.target.value})}/></Field>
                <Field label="Load (kN)"><input type="number" inputMode="decimal" value={c.loadKN||""} onChange={e=>setCube(i,{loadKN:e.target.value})}/></Field>
              </div>
            ))}
            <div style={{display:"flex",gap:8}}>
              <button className="pm-btn pm-btn--ghost pm-btn--full" onClick={()=>addCube("7")}>+ 7-day cube</button>
              <button className="pm-btn pm-btn--ghost pm-btn--full" onClick={()=>addCube("28")}>+ 28-day cube</button>
            </div>
          </>)}

          {form.testType==="pressure"&&(<>
            <div className="pm-form__divider">Pressure / Disinfection</div>
            <Field label="Medium"><input value={form.pressure.medium||""} onChange={e=>setSub("pressure",{medium:e.target.value})}/></Field>
            <Field label="Test Pressure"><input type="number" inputMode="decimal" value={form.pressure.testPressure||""} onChange={e=>setSub("pressure",{testPressure:e.target.value})}/></Field>
            <Field label="Units"><input value={form.pressure.units||""} onChange={e=>setSub("pressure",{units:e.target.value})}/></Field>
            <Field label="Duration (min)"><input type="number" inputMode="decimal" value={form.pressure.durationMinutes||""} onChange={e=>setSub("pressure",{durationMinutes:e.target.value})}/></Field>
            <Field label="Allowable Loss"><input type="number" inputMode="decimal" value={form.pressure.allowableLoss||""} onChange={e=>setSub("pressure",{allowableLoss:e.target.value})}/></Field>
            <Field label="Actual Loss"><input type="number" inputMode="decimal" value={form.pressure.actualLoss||""} onChange={e=>setSub("pressure",{actualLoss:e.target.value})}/></Field>
            <Field label="Result"><select value={form.pressure.result||""} onChange={e=>setSub("pressure",{result:e.target.value})}><option value="">Pending</option><option value="Pass">Pass</option><option value="Fail">Fail</option></select></Field>
          </>)}

          <div className="pm-form__divider">Notes</div>
          <Field label="Notes"><textarea value={form.notes||""} onChange={e=>set({notes:e.target.value})}/></Field>

          <button className="pm-btn pm-btn--primary pm-btn--full" disabled={saving} onClick={save}>{saving?"Saving…":(editId?"Update Test":"Save Test")}</button>
        </div>
      )}
    </div>
  );
}

// ─── Main shell ────────────────────────────────────────────────────────────────
export default function MobileApp({
  projects=[],selectedProject,setSelectedProject,currentProject,
  activeSection,setActiveSection,
  rfis=[],rfiForm,setRfiForm,handleAddRfi,handleEditRfi,handleDeleteRfi,editingRfiId,clearRfiForm,
  variations=[],handleCreateVariationFromRfi,
  diaries=[],diaryForm,setDiaryForm,handleAddDiary,handleEditDiary,handleDeleteDiary,editingDiaryId,clearDiaryForm,handleCopyPreviousDiary,
  ncrs=[],ncrForm,setNcrForm,handleAddOrUpdateNcr,handleEditNcr,handleDeleteNcr,editingNcrId,clearNcrForm,
  claims=[],claimForm,setClaimForm,handleAddOrUpdateClaim,handleEditClaim,handleDeleteClaim,editingClaimId,clearClaimForm,
  instructions=[],instructionForm,setInstructionForm,handleAddInstruction,handleEditInstruction,handleDeleteInstruction,editingInstructionId,clearInstructionForm,handleCreateVariationFromInstruction,
  variationForm,setVariationForm,handleAddOrUpdateVariation,handleEditVariation,handleDeleteVariation,editingVariationId,
  certificates=[],auditLogs=[],soqItems=[],boqRows=[],
  handleViewItem,currentUser,handleSignOut,isAdmin=false,
}){
  const [drawerOpen,setDrawerOpen]=useState(false);
  const [projectModalOpen,setProjectModalOpen]=useState(false);
  const contentRef=useRef(null);
  const meta=getSectionMeta(activeSection);

  const goTo=key=>{
    setActiveSection(key);
    setDrawerOpen(false);
    contentRef.current?.scrollTo({top:0,behavior:"smooth"});
  };

  useEffect(()=>{contentRef.current?.scrollTo({top:0});},[activeSection]);

  const S={currentProjectId:selectedProject,handleViewItem};

  const renderSection=()=>{
    switch(activeSection){
      case "home":          return <HomePanel currentProject={currentProject} goTo={goTo}/>;
      case "financial":     return <FinancialPanel {...S} currentProject={currentProject} boqRows={boqRows} variations={variations} claims={claims} certificates={certificates} soqItems={soqItems}/>;
      case "projectTracker":return <TrackerPanel {...S} currentProject={currentProject} rfis={rfis} variations={variations} claims={claims}/>;
      case "programme":     return <ProgrammePanel currentProject={currentProject}/>;
      case "users":         return isAdmin ? <UsersPanel/> : <SimplePanel icon="⊛" title="Restricted" sub="User Management is available to administrators only."/>;
      case "certificates":  return <CertificatesPanel {...S} certificates={certificates}/>;
      case "cashflow":      return <CashflowPanel {...S} certificates={certificates}/>;
      case "cpa":           return <SimplePanel icon="⊞" title="CPA" sub="Contract Price Adjustment tables available on desktop."/>;
      case "roe":           return <SimplePanel icon="⊟" title="ROE" sub="Rate of Exchange tables available on desktop."/>;
      case "variations":    return <VariationsPanel {...S} variations={variations} variationForm={variationForm} setVariationForm={setVariationForm} handleAddOrUpdateVariation={handleAddOrUpdateVariation} handleEditVariation={handleEditVariation} handleDeleteVariation={handleDeleteVariation} editingVariationId={editingVariationId}/>;
      case "claims":        return <ClaimsPanel {...S} claims={claims} claimForm={claimForm} setClaimForm={setClaimForm} handleAddOrUpdateClaim={handleAddOrUpdateClaim} handleEditClaim={handleEditClaim} handleDeleteClaim={handleDeleteClaim} editingClaimId={editingClaimId} clearClaimForm={clearClaimForm} currentProject={currentProject}/>;
      case "boq":           return <BOQPanel {...S} variations={variations} soqItems={soqItems}/>;
      case "soqImport":     return <SimplePanel icon="↑" title="SOQ Import" sub="File import available on desktop."/>;
      case "soqRegister":   return <SOQRegisterPanel {...S} soqItems={soqItems}/>;
      case "contract":      return <ContractPanel currentProject={currentProject}/>;
      case "rfis":          return <RFIPanel {...S} rfis={rfis} rfiForm={rfiForm} setRfiForm={setRfiForm} handleAddRfi={handleAddRfi} handleEditRfi={handleEditRfi} handleDeleteRfi={handleDeleteRfi} editingRfiId={editingRfiId} clearRfiForm={clearRfiForm} variations={variations} handleCreateVariationFromRfi={handleCreateVariationFromRfi}/>;
      case "instructions":  return <InstructionsPanel {...S} instructions={instructions} instructionForm={instructionForm} setInstructionForm={setInstructionForm} handleAddInstruction={handleAddInstruction} handleEditInstruction={handleEditInstruction} handleDeleteInstruction={handleDeleteInstruction} editingInstructionId={editingInstructionId} clearInstructionForm={clearInstructionForm} rfis={rfis} variations={variations} handleCreateVariationFromInstruction={handleCreateVariationFromInstruction} currentProject={currentProject}/>;
      case "diary":         return <DiaryPanel {...S} diaries={diaries} diaryForm={diaryForm} setDiaryForm={setDiaryForm} handleAddDiary={handleAddDiary} handleEditDiary={handleEditDiary} handleDeleteDiary={handleDeleteDiary} editingDiaryId={editingDiaryId} clearDiaryForm={clearDiaryForm} handleCopyPreviousDiary={handleCopyPreviousDiary}/>;
      case "ncr":           return <NCRPanel {...S} ncrs={ncrs} ncrForm={ncrForm} setNcrForm={setNcrForm} handleAddOrUpdateNcr={handleAddOrUpdateNcr} handleEditNcr={handleEditNcr} handleDeleteNcr={handleDeleteNcr} editingNcrId={editingNcrId} clearNcrForm={clearNcrForm} variations={variations}/>;
      case "env":           return <SimplePanel icon="◌" title="Environmental Audit" sub="Full audit forms available on desktop."/>;
      case "hs":            return <SimplePanel icon="+" title="Health & Safety" sub="Full H&S audit forms available on desktop."/>;
      case "audit":         return <AuditPanel auditLogs={auditLogs}/>;
      case "claude":        return <SimplePanel icon="✧" title="AI Review" sub="The Claude AI assistant is available on desktop."/>;
      case "risk":          return <RiskPanel {...S}/>;
      case "quality":       return <QualityPanel currentProjectId={selectedProject} currentUser={currentUser}/>;
      case "drawings":      return <div className="pm-embed"><DrawingRegisterSection currentProject={currentProject} currentProjectId={selectedProject}/></div>;
      case "defects":       return <div className="pm-embed"><DefectsSection currentProject={currentProject} currentProjectId={selectedProject} currentUser={currentUser}/></div>;
      case "mos":           return <div className="pm-embed"><MaterialsOnSiteSection currentProject={currentProject} currentProjectId={selectedProject} currentUser={currentUser} soqItems={soqItems} certificates={certificates}/></div>;
      case "completion":    return <div className="pm-embed"><CompletionDefectsSection currentProject={currentProject} currentProjectId={selectedProject}/></div>;
      case "timeBars":      return <div className="pm-embed"><TimeBarSection currentProject={currentProject} currentProjectId={selectedProject}/></div>;
      case "visualProgress":return <div className="pm-embed"><VisualProgressTracker selectedProject={selectedProject}/></div>;
      default:              return <EmptyState icon={meta.icon} title={meta.label} sub="Open on a larger screen for full access."/>;
    }
  };

  const projectName=currentProject?.name||currentProject?.id||"Select Project";

  return(
    <div className="pm-shell">
      <header className="pm-topbar">
        <div className="pm-topbar__left">
          <div className="pm-logo"><img src={`${process.env.PUBLIC_URL}/logo-mark.png`} alt="Celestia"/></div>
          <button className="pm-project-btn" onClick={()=>setProjectModalOpen(true)}>
            <span className="pm-project-btn__name">{projectName}</span>
            <span className="pm-project-btn__caret">▾</span>
          </button>
        </div>
        <div className="pm-topbar__right">
          <div className="pm-section-pill" style={{"--pill-color":meta.groupColor}}>{meta.icon} {meta.label}</div>
          {handleSignOut&&<button className="pm-icon-btn" onClick={handleSignOut} title="Sign out">⏻</button>}
        </div>
      </header>

      <main className="pm-content" ref={contentRef}>{renderSection()}</main>

      <nav className="pm-bottomnav">
        {PINNED.map(t=>(
          <button key={t.key} className={`pm-bottomnav__item${activeSection===t.key?" active":""}`} onClick={()=>goTo(t.key)}>
            <span className="pm-bottomnav__icon">{t.icon}</span>
            <span className="pm-bottomnav__label">{t.label}</span>
          </button>
        ))}
        <button className={`pm-bottomnav__item${drawerOpen?" active":""}`} onClick={()=>setDrawerOpen(true)}>
          <span className="pm-bottomnav__icon">⊞</span>
          <span className="pm-bottomnav__label">More</span>
        </button>
      </nav>

      <SectionsDrawer open={drawerOpen} onClose={()=>setDrawerOpen(false)} activeSection={activeSection} onNavigate={goTo} isAdmin={isAdmin}/>
      <ProjectModal open={projectModalOpen} onClose={()=>setProjectModalOpen(false)} projects={projects} selectedProject={selectedProject} setSelectedProject={setSelectedProject}/>
    </div>
  );
}
